import { API_PREFIX } from '@athenaeum/protocol/sync';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: Record<string, unknown>,
  ) {
    // Keep wire names and error codes intact while using Library's product terminology.
    super(
      message.replace(/\b(repositor(?:y|ies))\b/gi, (word) => {
        const label = word.toLowerCase() === 'repository' ? 'wiki' : 'wikis';
        return word[0] === word[0]?.toUpperCase() ? label[0]!.toUpperCase() + label.slice(1) : label;
      }),
    );
  }
}

/**
 * True when the answer says "the server is not there" rather than "the server said no": no
 * connection, a proxy's 5xx page, or a reply that is not the Library API at all. Pages use it to
 * offer "try again" instead of pretending the person did something wrong.
 */
export const isUnavailable = (error: unknown): boolean =>
  error instanceof ApiError && (error.status === 0 || error.status >= 502 || error.code === 'bad_response');

export async function api<T = unknown>(method: string, path: string, body?: unknown): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${API_PREFIX}${path}`, {
      method,
      credentials: 'same-origin',
      headers: body === undefined ? undefined : { 'content-type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new ApiError(
      0,
      'offline',
      'The Library server cannot be reached. Check your connection and try again.',
    );
  }
  const text = await response.text();
  let parsed: unknown = null;
  let json = true;
  try {
    parsed = text ? JSON.parse(text) : null;
  } catch {
    json = false; // a proxy's HTML error page, or the web app's own page where the API should be
  }
  if (!response.ok) {
    const candidate = parsed && typeof parsed === 'object' && 'error' in parsed ? parsed.error : null;
    const error = candidate && typeof candidate === 'object' ? candidate : null;
    if (response.status === 401 && !path.startsWith('/auth/') && path !== '/user') {
      window.dispatchEvent(new Event('athenaeum-session-expired'));
    }
    if (!error && response.status >= 502) {
      throw new ApiError(
        response.status,
        'unavailable',
        'The Library server is not reachable right now. Try again in a moment.',
      );
    }
    throw new ApiError(
      response.status,
      error && 'code' in error && typeof error.code === 'string' ? error.code : 'error',
      error && 'message' in error && typeof error.message === 'string'
        ? error.message
        : `The server answered ${response.status}.`,
      error &&
        'details' in error &&
        error.details !== null &&
        typeof error.details === 'object' &&
        !Array.isArray(error.details)
        ? (error.details as Record<string, unknown>)
        : undefined,
    );
  }
  if (!json) {
    throw new ApiError(
      0,
      'bad_response',
      'The server answered, but not like an Athenaeum Library API. It may be misconfigured or still starting.',
    );
  }
  return parsed as T;
}

export const get = <T>(path: string) => api<T>('GET', path);
export const post = <T>(path: string, body: unknown = {}) => api<T>('POST', path, body);
export const patch = <T>(path: string, body: unknown) => api<T>('PATCH', path, body);
export const put = <T>(path: string, body: unknown) => api<T>('PUT', path, body);
export const del = <T>(path: string) => api<T>('DELETE', path);

export type * from '../shared/types/api';
